import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { Cite, Cited, citeLinks, clock, timeOf, withCiteLinks } from "../components/Cited";
import { MeetingChat } from "../components/MeetingChat";
import { AddToProject } from "../components/ProjectDialogs";
import { IconPencil, RenameMeeting } from "../components/RenameMeeting";
import { ShareDialog } from "../components/ShareDialog";
import { SpeakerMenu } from "../components/SpeakerMenu";
import {
  endpoints,
  fileUrl,
  type MeetingDetail as Detail,
  type MeetingPlatform,
  type MinutesDocument,
  type SpeakerName,
  type Utterance,
} from "../lib/api";
import { platformLabel } from "../lib/calendar";
import { duration } from "../lib/format";

/** The sections of the page, in order, for "On this page". */
const SECTIONS = [
  { id: "minutes", label: "Minutes" },
  { id: "actions", label: "Action items" },
  { id: "for-you", label: "Personalized details" },
  { id: "transcript", label: "Transcript" },
] as const;
type SectionId = (typeof SECTIONS)[number]["id"];

/** One meeting as one long document: the minutes, the action items, your
 * personalized details and the transcript, one after another, with the
 * recording beside them the whole way down.
 *
 * Everything here comes from /api/recordings/<session-id>, which only answers
 * for people who were in the meeting — so there's nothing to check in the
 * browser; a meeting you may not see simply 404s. */
export function MeetingDetail() {
  const { sessionId = "" } = useParams();
  const queryClient = useQueryClient();
  const video = useRef<HTMLVideoElement>(null);
  const audio = useRef<HTMLAudioElement>(null);
  // The page's own scrolling area (the app shell doesn't scroll the window).
  const page = useRef<HTMLDivElement>(null);
  // Where the recording is, to mark the line being spoken in the transcript.
  const [playhead, setPlayhead] = useState<number | null>(null);
  // A signed link lasts four hours. Someone who leaves the page open longer
  // and then presses play gets a 403 — so fetch a fresh one, once, rather
  // than showing a dead player. Once, because if the second link fails too
  // the problem isn't expiry.
  const retriedExpiredLink = useRef(false);
  const [section, setSection] = useState<SectionId>("minutes");
  // Already loaded by the app shell; this only reads it, for the CSRF token.
  const me = useQuery({ queryKey: ["me"], queryFn: endpoints.me, staleTime: Infinity });

  const { data: fresh, error, isLoading, dataUpdatedAt } = useQuery({
    queryKey: ["recording", sessionId],
    queryFn: () => endpoints.recording(sessionId),
    // While the bot is still in the call, or the transcript or minutes are on
    // their way, this page is worth refreshing; a finished meeting never changes.
    refetchInterval: (query) => {
      const meeting = query.state.data;
      const writing = meeting?.minutes?.status === "queued" || meeting?.minutes?.status === "running";
      const personal = meeting?.personal?.status === "queued" || meeting?.personal?.status === "running";
      const roman = meeting?.transcript_roman?.status === "queued" || meeting?.transcript_roman?.status === "running";
      const naming = meeting?.speakers?.status === "queued" || meeting?.speakers?.status === "running";
      return meeting && (meeting.active || meeting.transcript === "pending" || writing || personal || roman || naming)
        ? 10_000 : false;
    },
  });
  const data = useSteadyLinks(fresh, sessionId, dataUpdatedAt);

  // ?t=754: opened from a moment cited in a project's Overview or Timeline —
  // go there once the player can, and play if the browser lets it.
  const [params] = useSearchParams();
  const startAt = params.get("t") !== null ? Number(params.get("t")) : NaN;
  const jumped = useRef(false);
  const hasPlayer = Boolean(data && (data.has_video || data.has_audio));
  useEffect(() => {
    const player: HTMLMediaElement | null = video.current ?? audio.current;
    if (jumped.current || !hasPlayer || !Number.isFinite(startAt) || !player) return;
    jumped.current = true;
    const go = () => {
      player.currentTime = startAt;
      player.play().catch(() => {
        /* the browser may refuse to play by itself; the seek still happened */
      });
    };
    if (player.readyState >= 1) go();
    else player.addEventListener("loadedmetadata", go, { once: true });
  }, [hasPlayer, startAt]);

  const generatePersonal = useMutation({
    mutationFn: () => endpoints.generatePersonal(sessionId, me.data?.csrf_token ?? ""),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["recording", sessionId] }),
  });

  // Marks the section being read in "On this page": the last one whose top
  // has passed a line a quarter of the way down — or the last section once
  // the page is scrolled to the bottom, since a short last section never
  // reaches that line.
  const loaded = Boolean(data);
  useEffect(() => {
    const scroller = page.current;
    if (!loaded || !scroller) return;
    const update = () => {
      const line = scroller.getBoundingClientRect().top + scroller.clientHeight / 4;
      let current: SectionId = SECTIONS[0].id;
      for (const { id } of SECTIONS) {
        const element = document.getElementById(id);
        if (element && element.getBoundingClientRect().top <= line) current = id;
      }
      if (scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 4) current = SECTIONS[SECTIONS.length - 1].id;
      setSection(current);
    };
    update();
    scroller.addEventListener("scroll", update, { passive: true });
    return () => scroller.removeEventListener("scroll", update);
  }, [loaded]);

  /** Plays the recording from `seconds`, bringing the player into view on a
   * narrow screen, where it sits above the document instead of beside it. */
  function playFrom(seconds: number) {
    const player: HTMLMediaElement | null = video.current ?? audio.current;
    if (!player) return;
    player.currentTime = seconds;
    player.play().catch(() => {
      /* the browser may refuse to play by itself; the seek still happened */
    });
    if (player.getBoundingClientRect().top < 0) player.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function linkFailed() {
    if (!data?.video_url || retriedExpiredLink.current) return;
    retriedExpiredLink.current = true;
    data.renewLinks();
    queryClient.invalidateQueries({ queryKey: ["recording", sessionId] });
  }

  if (error || isLoading || !data) {
    return (
      <div className="page-body">
        {error && <div className="error">{(error as Error).message}</div>}
        {isLoading && <div className="empty">Loading…</div>}
      </div>
    );
  }

  const csrfToken = me.data?.csrf_token ?? "";
  // Times in the document play the recording from there, when there's one to play.
  // Sound only (in person, or an upload whose video wasn't kept) plays in an audio player.
  const soundOnly = data.in_person || (Boolean(data.uploaded) && !data.has_video);
  const seek = data.has_video || (soundOnly && data.has_audio) ? playFrom : undefined;
  return (
    <div className="page-body doc-page" ref={page}>
      <div className="doc">
        <aside className="doc-rail">
          {soundOnly ? (
            <RoomAudio meeting={data} audio={audio} onTime={setPlayhead} />
          ) : (
            <Recording meeting={data} video={video} onTime={setPlayhead} onLinkFailed={linkFailed} />
          )}
          <nav className="doc-toc" aria-label="On this page">
            <span className="doc-label">On this page</span>
            {SECTIONS.map(({ id, label }) => (
              <a
                key={id}
                href={`#${id}`}
                className={section === id ? "on" : undefined}
                onClick={(event) => {
                  event.preventDefault();
                  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
              >
                {label}
              </a>
            ))}
          </nav>
          <Readiness meeting={data} />
        </aside>

        <article className="doc-main">
          <Header meeting={data} csrfToken={csrfToken}
                  canEdit={Boolean(me.data && (me.data.is_admin
                    || me.data.email.toLowerCase() === data.owner_email?.toLowerCase()))} />
          <section id="minutes" className="doc-section">
            <Minutes meeting={data} csrfToken={csrfToken} onSeek={seek} />
          </section>
          <section id="actions" className="doc-section">
            <ActionItems meeting={data} onSeek={seek} />
          </section>
          <section id="for-you" className="doc-section">
            <Personal
              meeting={data}
              onGenerate={() => generatePersonal.mutate()}
              asking={generatePersonal.isPending}
              askError={generatePersonal.error as Error | null}
              onSeek={seek}
            />
          </section>
          <section id="transcript" className="doc-section">
            <Transcript meeting={data} playhead={playhead}
                        onSeek={seek}
                        csrfToken={csrfToken} />
          </section>
        </article>
      </div>

      {data.session_id && (
        <MeetingChat sessionId={data.session_id} csrfToken={csrfToken} ready={data.transcript === "ready"} />
      )}
    </div>
  );
}

/** Breadcrumb, title, the facts in one line, and the meeting's state when
 * there's something to say about it. */
function Header({ meeting, csrfToken, canEdit }: { meeting: Detail; csrfToken: string; canEdit: boolean }) {
  const [sharing, setSharing] = useState(false);
  const [filing, setFiling] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const facts = [
    when(meeting.starts_at ?? meeting.requested_at),
    meeting.duration_seconds != null ? duration(meeting.duration_seconds) : null,
    // An upload says its app beside its Uploaded tag, where it can be changed.
    meeting.uploaded ? null : platformLabel(meeting),
    meeting.owner_email ? `${meeting.uploaded ? "uploaded" : "requested"} by ${meeting.owner_email}` : null,
  ].filter(Boolean);
  return (
    <header className="doc-header">
      <div className="doc-header-top">
        <div className="doc-crumbs">
          <Link to="/meetings">Meetings</Link>
          <span aria-hidden>/</span>
        </div>
        {meeting.session_id && (
          <div className="doc-header-actions">
            <button className="btn-secondary doc-share" onClick={() => setFiling(true)}>
              <IconFolder />
              Add to project
            </button>
            <button className="btn-secondary doc-share" onClick={() => setSharing(true)}>
              <IconShare />
              Share
            </button>
          </div>
        )}
      </div>
      {filing && meeting.session_id && (
        <AddToProject
          sessionId={meeting.session_id}
          title={meeting.title ?? "Untitled meeting"}
          inProjects={meeting.projects ?? []}
          csrfToken={csrfToken}
          onClose={() => setFiling(false)}
        />
      )}
      {sharing && meeting.session_id && (
        <ShareDialog
          sessionId={meeting.session_id}
          title={meeting.title ?? "Untitled meeting"}
          csrfToken={csrfToken}
          onClose={() => setSharing(false)}
        />
      )}
      {renaming && meeting.session_id ? (
        <RenameMeeting sessionId={meeting.session_id} title={meeting.title ?? ""} csrfToken={csrfToken} large
                       onDone={() => setRenaming(false)} />
      ) : (
        <h1 className="doc-title">
          {meeting.title ?? "Untitled meeting"}
          {meeting.can_rename && (
            <button className="title-edit" onClick={() => setRenaming(true)} aria-label="Rename this meeting"
                    title="Rename">
              <IconPencil />
            </button>
          )}
        </h1>
      )}
      {(meeting.projects ?? []).length > 0 && (
        <p className="doc-projects">
          {(meeting.projects ?? []).map((project) => (
            <Link key={project.id} className="project-chip" to={`/projects/${project.id}`}>
              <IconFolder />
              {project.name}
            </Link>
          ))}
        </p>
      )}
      <p className="doc-facts">
        {meeting.uploaded && (
          <>
            <span className="upload-tag">Uploaded</span>{" "}
            {canEdit && meeting.session_id
              ? <UploadPlatform sessionId={meeting.session_id} platform={meeting.platform ?? "other"} csrfToken={csrfToken} />
              : meeting.platform !== "other" && platformLabel(meeting)}
            {" · "}
          </>
        )}
        {facts.join(" · ")}
        {meeting.state !== "finished" && (
          <span className={`status ${meeting.state}`}>
            <span className="status-dot" aria-hidden />
            {meeting.label}
          </span>
        )}
      </p>
      {meeting.upload_note && <p className="upload-note">{meeting.upload_note}</p>}
    </header>
  );
}

const UPLOAD_PLATFORMS: { value: MeetingPlatform; label: string }[] = [
  { value: "meet", label: "Google Meet" },
  { value: "teams", label: "Microsoft Teams" },
  { value: "zoom", label: "Zoom" },
  { value: "in_person", label: "In person" },
  { value: "other", label: "Another app" },
];

/** Which app an upload was recorded in: worked out from its video, or what
 * the uploader said — changeable by them when that was wrong. */
function UploadPlatform({ sessionId, platform: current, csrfToken }: {
  sessionId: string;
  platform: MeetingPlatform;
  csrfToken: string;
}) {
  const queryClient = useQueryClient();
  const change = useMutation({
    mutationFn: (next: MeetingPlatform) => endpoints.setUploadPlatform(sessionId, next, csrfToken),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recording", sessionId] });
      queryClient.invalidateQueries({ queryKey: ["recordings"] });
    },
  });
  return (
    <select className="upload-platform" value={change.isPending ? change.variables : current} disabled={change.isPending}
            aria-label="Recorded in" title="Which app it was recorded in — change it if this is wrong"
            onChange={(event) => change.mutate(event.target.value as MeetingPlatform)}>
      {UPLOAD_PLATFORMS.map((choice) => <option key={choice.value} value={choice.value}>{choice.label}</option>)}
    </select>
  );
}

type SteadyDetail = Detail & { renewLinks: () => void };

/** The meeting, with the same video and audio links from one refresh to the
 * next. Every answer from the server carries freshly signed S3 links, and
 * this page refreshes every ten seconds while something's on its way (the
 * transcript, minutes, Roman Urdu): handed a new link, the player started
 * over each time, back at 0:00. So the first links are kept, a link that was
 * missing is taken when it arrives, and new ones replace them only after
 * `renewLinks()`, once the old ones have failed. */
function useSteadyLinks(meeting: Detail | undefined, sessionId: string, updatedAt: number): SteadyDetail | undefined {
  const links = useRef<{ sessionId: string; video: string | null; audio: string | null } | null>(null);
  const renewAfter = useRef(0);
  if (meeting) {
    const kept = links.current?.sessionId === sessionId ? links.current : null;
    const renew = renewAfter.current > 0 && updatedAt > renewAfter.current;
    links.current = {
      sessionId,
      video: (!renew && kept?.video) || meeting.video_url,
      audio: (!renew && kept?.audio) || meeting.audio_url,
    };
    if (renew) renewAfter.current = 0;
  }
  if (!meeting || !links.current) return undefined;
  return {
    ...meeting,
    video_url: links.current.video,
    audio_url: links.current.audio,
    renewLinks: () => {
      renewAfter.current = Date.now();
    },
  };
}

/** The recording, inline and large, beside the document the whole way down.
 * A fixed 16:10 shape (the bot records 1280x800), so the player is full size
 * from the first frame — without it, a long meeting's player sat at zero
 * height while its index downloaded, and looked like no video at all. */
function Recording({ meeting, video, onTime, onLinkFailed }: {
  meeting: Detail;
  video: React.RefObject<HTMLVideoElement | null>;
  onTime: (seconds: number) => void;
  onLinkFailed: () => void;
}) {
  if (!meeting.has_video || !meeting.session_id) {
    return (
      <div className="doc-video empty-video">
        {meeting.active ? "The recording appears here once the meeting ends." : "No video was saved for this meeting."}
      </div>
    );
  }
  return (
    <video
      ref={video}
      className="doc-video"
      controls
      preload="metadata"
      src={meeting.video_url ?? fileUrl(meeting.session_id, "video")}
      // Only the app's own copy needs the session cookie. A signed S3 link
      // carries its own permission, and asking for credentials there would
      // fail CORS instead.
      {...(meeting.video_url ? {} : { crossOrigin: "use-credentials" as const })}
      onTimeUpdate={(event) => onTime(event.currentTarget.currentTime)}
      onError={onLinkFailed}
    />
  );
}

/** An in-person meeting's recording: the room's audio, with a player that
 * the transcript's timestamps can jump. */
function RoomAudio({ meeting, audio, onTime }: {
  meeting: Detail;
  audio: React.RefObject<HTMLAudioElement | null>;
  onTime: (seconds: number) => void;
}) {
  return (
    <div className="doc-video doc-audio">
      <span className="doc-audio-ring" aria-hidden />
      <p>{meeting.uploaded ? "Uploaded recording — sound only"
        : meeting.active ? "Recording in person — the audio appears here once it's stopped." : "Recorded in person"}</p>
      {meeting.has_audio && meeting.session_id && !meeting.active && (
        <audio
          ref={audio}
          controls
          preload="metadata"
          src={meeting.audio_url ?? fileUrl(meeting.session_id, "audio")}
          {...(meeting.audio_url ? {} : { crossOrigin: "use-credentials" as const })}
          onTimeUpdate={(event) => onTime(event.currentTarget.currentTime)}
        />
      )}
    </div>
  );
}

/** What's ready, and the files to take away. */
function Readiness({ meeting }: { meeting: Detail }) {
  const minutes = meeting.minutes?.document ? "Minutes ready" : minutesStatus(meeting);
  const transcript = { ready: "Transcript ready", pending: "Transcribing…", none: "No transcript" }[meeting.transcript];
  const id = meeting.session_id;
  return (
    <div className="doc-ready">
      <p>
        {transcript} · {minutes}
        {(meeting.emailed ?? 0) > 0 && ` · emailed to ${meeting.emailed} ${meeting.emailed === 1 ? "person" : "people"}`}
      </p>
      {id && (
        <p className="doc-files">
          {meeting.has_audio && <a href={meeting.audio_url ?? fileUrl(id, "audio")}>Audio</a>}
          {meeting.transcript === "ready" && <a href={fileUrl(id, "transcript")}>Transcript</a>}
          {meeting.minutes?.document && <a href={fileUrl(id, "minutes")}>Minutes</a>}
        </p>
      )}
    </div>
  );
}

function minutesStatus(meeting: Detail): string {
  const status = meeting.minutes?.status;
  if (status === "queued" || status === "running") return "Writing minutes…";
  if (status === "failed") return "Minutes failed";
  return "No minutes";
}

/** The minutes of meeting, and where they've got to. They're written by
 * themselves once the transcript is done; Regenerate writes them again. */
function Minutes({ meeting, csrfToken, onSeek }: {
  meeting: Detail;
  csrfToken: string;
  onSeek?: (seconds: number) => void;
}) {
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState(false);
  const regenerate = useMutation({
    mutationFn: () => endpoints.regenerateMinutes(meeting.session_id!, csrfToken),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["recording", meeting.session_id] }),
  });

  const state = meeting.minutes;
  const writing = state?.status === "queued" || state?.status === "running";
  // Not for skipped minutes: the same rule (too short, no speech) would skip
  // them again.
  const canWrite = meeting.transcript === "ready" && !writing && state?.status !== "skipped";

  let note: string | null = null;
  if (writing) note = state?.document ? "Writing new minutes — these stay until they're ready." : null;
  else if (state?.status === "failed") note = `The last attempt failed: ${state.error ?? "no reason given"}`;
  else if (state?.status === "skipped") note = `No minutes: ${state.error}.`;

  async function copy() {
    if (!state?.document) return;
    await navigator.clipboard.writeText(minutesText(state.document));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  if (!state?.document) {
    let message: string;
    if (writing) message = "Writing the minutes — about a minute. The page updates itself.";
    else if (state?.status === "failed" || state?.status === "skipped") message = note as string;
    else if (meeting.transcript === "pending") message = "The minutes are written once the transcript is ready.";
    else if (meeting.transcript === "ready") message = "No minutes yet.";
    else message = "No minutes: there's no transcript to write them from.";
    return (
      <>
        <div className="doc-section-head">
          <span className="doc-label">Minutes of meeting</span>
        </div>
        <p className="doc-empty">{message}</p>
        {writing && <div className="doc-progress" aria-hidden />}
        {regenerate.error && <div className="error">{(regenerate.error as Error).message}</div>}
        {canWrite && (
          <button className="btn-primary" onClick={() => regenerate.mutate()} disabled={regenerate.isPending}>
            {regenerate.isPending ? "Asking…" : "Write minutes"}
          </button>
        )}
      </>
    );
  }

  const { info } = state.document;
  const start = info.starts_at ? new Date(info.starts_at) : null;
  return (
    <>
      <div className="doc-section-head">
        <span className="doc-label">Minutes of meeting</span>
        <span className="doc-actions">
          <button className="btn-text" onClick={copy}>{copied ? "Copied" : "Copy"}</button>
          <a className="btn-text" href={fileUrl(meeting.session_id!, "minutes")}>Download</a>
        </span>
      </div>
      <p className="doc-ai">
        <Star />
        <span>Written by AI from the transcript{state.model ? ` (${state.model})` : ""}.</span>
        {canWrite && (
          <button className="btn-text" onClick={() => regenerate.mutate()} disabled={regenerate.isPending}>
            {regenerate.isPending ? "Asking…" : "Regenerate"}
          </button>
        )}
      </p>
      {note && <p className="doc-note">{note}</p>}
      {regenerate.error && <div className="error">{(regenerate.error as Error).message}</div>}

      <dl className="doc-info">
        <div>
          <dt>Date</dt>
          <dd>{start ? start.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—"}</dd>
        </div>
        <div>
          <dt>Time</dt>
          <dd>{start ? start.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : "—"}</dd>
        </div>
        <div>
          <dt>Duration</dt>
          <dd>{info.duration_minutes ? `${info.duration_minutes} min` : "—"}</dd>
        </div>
        <div>
          <dt>Platform</dt>
          <dd>{info.platform}</dd>
        </div>
        {info.link && (
          <div className="wide">
            <dt>Meeting link</dt>
            <dd>
              <a href={info.link} target="_blank" rel="noreferrer">{info.link.replace("https://", "")}</a>
            </dd>
          </div>
        )}
      </dl>

      <h3 className="doc-sub">Attendees</h3>
      {state.document.attendees.length ? (
        <ul className="doc-people">
          {state.document.attendees.map((name, i) => (
            <li key={name}>
              <span className={`initials c${i % 4}`}>{initials(name)}</span>
              {name}
            </li>
          ))}
        </ul>
      ) : (
        <p className="doc-empty">Not recorded.</p>
      )}

      <h3 className="doc-sub">Agenda</h3>
      {state.document.agenda.length ? (
        <ol className="doc-list">{state.document.agenda.map((item, i) => <li key={i}>{item}</li>)}</ol>
      ) : (
        <p className="doc-empty">None stated.</p>
      )}

      <h3 className="doc-sub">Discussion</h3>
      {state.document.discussion.length ? (
        state.document.discussion.map((point, i) => (
          <div className="doc-topic" key={i}>
            <h4>{point.topic}</h4>
            <p><Cited text={point.summary} onSeek={onSeek} /></p>
          </div>
        ))
      ) : (
        <p className="doc-empty">Nothing substantive was discussed.</p>
      )}
    </>
  );
}

function ActionItems({ meeting, onSeek }: { meeting: Detail; onSeek?: (seconds: number) => void }) {
  const items = meeting.minutes?.document?.action_items;
  // Minutes written before owners have none: no empty column for those.
  const owners = items?.some((item) => item.owner) ?? false;
  return (
    <>
      <div className="doc-section-head">
        <span className="doc-label">Action items</span>
      </div>
      {!items ? (
        <p className="doc-empty">Listed here once the minutes are written.</p>
      ) : items.length === 0 ? (
        <p className="doc-empty">No action items came out of this meeting.</p>
      ) : (
        <table className="doc-table">
          <thead>
            <tr>
              <th>Task</th>
              {owners && <th className="owner">Owner</th>}
              <th className="due">Due</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <tr key={i}>
                <td>
                  <Cited text={item.task} onSeek={onSeek} />
                  {timeOf(item.said_at) !== null && <Cite seconds={timeOf(item.said_at)!} onSeek={onSeek} />}
                </td>
                {owners && <td className="owner">{item.owner ?? "—"}</td>}
                <td className="due">{item.due ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

/** Your Personalized Details: this meeting, read against your own instruction.
 * Only you see them. Written when you ask, not before. */
function Personal({ meeting, onGenerate, asking, askError, onSeek }: {
  meeting: Detail;
  onGenerate: () => void;
  asking: boolean;
  askError: Error | null;
  onSeek?: (seconds: number) => void;
}) {
  const state = meeting.personal;
  const writing = asking || state.status === "queued" || state.status === "running";
  const instruction = useQuery({ queryKey: ["personalization"], queryFn: endpoints.personalization });

  const head = (
    <div className="doc-section-head">
      <span className="doc-label">Personalized details</span>
    </div>
  );

  if (!state.has_instruction) {
    return (
      <>
        {head}
        <p className="doc-empty">
          Tell the app who you are and what you want from meetings, and this shows only what matters to you.
        </p>
        <Link className="btn-primary" to="/personalization">Set up personalization</Link>
      </>
    );
  }

  let note: string | null = null;
  if (writing && state.content) note = "Writing new details — these stay until they're ready.";
  else if (state.status === "failed") note = `The last attempt failed: ${state.error ?? "no reason given"}`;
  else if (state.instruction_changed) note = "You've changed your instructions since these were written.";
  else if (state.names_changed) note = "Speaker names have changed since these were written — generate again to use them.";

  const canWrite = meeting.transcript === "ready" && !writing;
  return (
    <>
      {head}
      {instruction.data?.text && (
        <blockquote className="doc-instruction">
          <p>“{instruction.data.text}”</p>
          <Link to="/personalization">Edit</Link>
        </blockquote>
      )}
      {note && <p className="doc-note">{note}</p>}
      {askError && <div className="error">{askError.message}</div>}
      {canWrite && (
        <button className={state.content ? "btn-secondary" : "btn-primary"} onClick={onGenerate}>
          <Star />
          {state.content ? "Write again" : state.status === "failed" ? "Try again" : "Generate for me"}
        </button>
      )}
      {!state.content && (
        <p className="doc-empty">
          {writing
            ? "Reading the meeting for you — a minute or two. The page updates itself."
            : meeting.transcript !== "ready"
              ? "Personalized details are written from the transcript, once it's ready."
              : "Not written yet. Only you will see them."}
        </p>
      )}
      {writing && !state.content && <div className="doc-progress" aria-hidden />}
      {state.content && (
        // Markdown from the model. react-markdown never renders raw HTML, so
        // whatever the model writes, it can't inject anything into the page.
        <div className="markdown doc-markdown">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={citeLinks(onSeek)}>
            {withCiteLinks(state.content)}
          </ReactMarkdown>
        </div>
      )}
    </>
  );
}

function Transcript({ meeting, playhead, onSeek, csrfToken }: {
  meeting: Detail;
  playhead: number | null;
  onSeek?: (seconds: number) => void;
  csrfToken: string;
}) {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState("");
  // The line whose speaker is being renamed: the menu opens under it.
  const [renaming, setRenaming] = useState<number | null>(null);
  const utterances = meeting.transcript_data?.utterances ?? [];
  const names = meeting.speakers?.names ?? {};
  // Who a voice is, where that's known; its label where it isn't.
  const nameOf = (label: string) => names[label]?.name ?? label;
  const needle = query.trim().toLowerCase();
  const shown = useMemo(
    () => (needle
      ? utterances.filter((u) => u.text.toLowerCase().includes(needle) || u.speaker.toLowerCase().includes(needle)
        || (names[u.speaker]?.name ?? "").toLowerCase().includes(needle))
      : utterances),
    [utterances, needle, names],
  );
  const rename = useMutation({
    mutationFn: ({ label, name }: { label: string; name: string | null }) =>
      endpoints.renameSpeaker(meeting.session_id!, label, name, csrfToken),
    onSuccess: (speakers) => {
      queryClient.setQueryData(["recording", meeting.session_id], (old: Detail | undefined) =>
        old ? { ...old, speakers } : old);
      // The minutes are now queued for a rewrite: show it, and refresh until it's done.
      queryClient.invalidateQueries({ queryKey: ["recording", meeting.session_id] });
      setRenaming(null);
    },
  });
  // The line being spoken: the last one that started at or before the playhead.
  const current = useMemo(() => {
    if (playhead == null || playhead < 0.5) return -1;
    let index = -1;
    for (let i = 0; i < utterances.length && utterances[i].start <= playhead; i++) index = i;
    return index;
  }, [utterances, playhead]);
  const speakers = useMemo(() => {
    const order = new Map<string, number>();
    for (const u of utterances) if (!order.has(u.speaker)) order.set(u.speaker, order.size);
    return order;
  }, [utterances]);

  let body: React.ReactNode;
  if (meeting.transcript === "pending") body = <p className="doc-empty">Transcribing — this takes a few minutes. The page updates itself.</p>;
  else if (meeting.transcript === "none" || !meeting.transcript_data)
    body = (
      <p className="doc-empty">
        {meeting.active
          ? "The transcript is made once the meeting ends."
          : "No transcript. Either nobody spoke, or the recording hasn't been transcribed."}
      </p>
    );
  else if (utterances.length === 0) body = <p className="doc-empty">The recording was transcribed, but no speech was found in it.</p>;
  else if (shown.length === 0) body = <p className="doc-empty">Nothing matches “{query.trim()}”.</p>;
  else
    body = (
      <div className="doc-transcript">
        {shown.map((utterance) => {
          const index = utterances.indexOf(utterance);
          return (
            <Turn
              key={`${utterance.start}-${index}`}
              utterance={utterance}
              name={nameOf(utterance.speaker)}
              named={names[utterance.speaker]}
              colour={(speakers.get(utterance.speaker) ?? 0) % 6}
              current={index === current}
              needle={needle}
              onSeek={onSeek}
              onRename={meeting.session_id ? () => {
                rename.reset();
                setRenaming(index);
              } : undefined}
            >
              {renaming === index && (
                <SpeakerMenu
                  label={utterance.speaker}
                  current={names[utterance.speaker]?.name ?? null}
                  candidates={meeting.speakers?.candidates ?? []}
                  saving={rename.isPending}
                  error={rename.error ? (rename.error as Error).message : null}
                  onSave={(name) => rename.mutate({ label: utterance.speaker, name })}
                  onClose={() => setRenaming(null)}
                />
              )}
            </Turn>
          );
        })}
      </div>
    );

  const data = meeting.transcript_data;
  const roman = meeting.transcript_roman;
  const converting = roman?.status === "queued" || roman?.status === "running";
  const naming = meeting.speakers?.status === "queued" || meeting.speakers?.status === "running";
  const anyNamed = Object.values(names).some((entry) => entry.name);
  return (
    <>
      <div className="doc-section-head">
        <span className="doc-label">Transcript</span>
        {utterances.length > 0 && (
          <input
            className="doc-search"
            type="search"
            placeholder="Find in transcript"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            aria-label="Find in transcript"
          />
        )}
      </div>
      {data && utterances.length > 0 && (
        <p className="doc-note">
          {needle
            ? `${shown.length} of ${utterances.length} lines match`
            : `${utterances.length} lines · ${data.speakers.length} speaker${data.speakers.length === 1 ? "" : "s"}`}
          {roman?.shown ? " · Roman Urdu" : data.language_code && ` · ${data.language_code.toUpperCase()}`}
          {onSeek && " · click a time to play from there"}
        </p>
      )}
      {roman && utterances.length > 0 && (roman.shown || converting) && (
        <p className="doc-ai">
          <Star />
          <span>
            {roman.shown
              ? "Hindi and Urdu script written in Roman Urdu by AI. The download has the original."
              : "Writing the Hindi and Urdu script in Roman Urdu…"}
          </span>
        </p>
      )}
      {utterances.length > 0 && meeting.speakers && (
        <p className="doc-ai">
          <Star />
          <span>
            {naming
              ? "Working out who's speaking from the conversation…"
              : anyNamed
                ? "Speaker names worked out by AI from the conversation, or set by people here. Click a name to correct it."
                : "Nobody could be named from the conversation. Click a speaker to name them."}
          </span>
        </p>
      )}
      {body}
    </>
  );
}

function Turn({ utterance, name, named, colour, current, needle, onSeek, onRename, children }: {
  utterance: Utterance;
  /** Who's speaking: their name, or the label when nobody knows. */
  name: string;
  named?: SpeakerName;
  colour: number;
  current: boolean;
  needle: string;
  onSeek?: (seconds: number) => void;
  onRename?: () => void;
  children?: React.ReactNode;
}) {
  const why = named?.by === "person"
    ? named.name ? `Named by ${named.by_user ?? "someone here"}.` : `${named.by_user ?? "Someone"} wasn't sure who this is.`
    : named?.name ? `Named by AI from: ${named.evidence ?? "the conversation"}` : "Nobody named this speaker yet.";
  return (
    <div className={`turn${current ? " current" : ""}`} data-speaker={colour}>
      {onSeek ? (
        <button className="stamp" onClick={() => onSeek(utterance.start)} title="Play from here">
          {clock(utterance.start)}
        </button>
      ) : (
        <span className="stamp">{clock(utterance.start)}</span>
      )}
      <div>
        <span className="speaker-wrap">
          {onRename ? (
            <button className="speaker" onClick={onRename} title={`${why} Click to change.`}
                    aria-label={`${name} — change who this is`}>
              <span className="speaker-dot" aria-hidden />
              {name}
              {named?.by === "ai" && named.name && <span className="speaker-ai" aria-hidden>✦</span>}
            </button>
          ) : (
            <span className="speaker" title={why}>
              <span className="speaker-dot" aria-hidden />
              {name}
            </span>
          )}
          {children}
        </span>
        <p>{highlight(utterance.text, needle)}</p>
      </div>
    </div>
  );
}

/** The search text marked wherever it appears in `text`. */
function highlight(text: string, needle: string): React.ReactNode {
  if (!needle) return text;
  const parts: React.ReactNode[] = [];
  const lower = text.toLowerCase();
  let from = 0;
  for (let at = lower.indexOf(needle); at !== -1; at = lower.indexOf(needle, from)) {
    parts.push(text.slice(from, at), <mark key={at}>{text.slice(at, at + needle.length)}</mark>);
    from = at + needle.length;
  }
  parts.push(text.slice(from));
  return parts;
}

/** The brand's four-point star: marks what AI wrote, and nothing else. */
function IconFolder() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
    </svg>
  );
}

function IconShare() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="9" cy="8" r="3.5" />
      <path d="M3 20a6 6 0 0112 0M18 8v6M15 11h6" />
    </svg>
  );
}

function Star() {
  return (
    <svg className="star" viewBox="0 0 16 16" width="13" height="13" aria-hidden>
      <path d="M8 0L9.4 6.6L16 8L9.4 9.4L8 16L6.6 9.4L0 8L6.6 6.6Z" fill="currentColor" />
    </svg>
  );
}

/** The minutes as plain text, for Copy. */
function minutesText(minutes: MinutesDocument): string {
  const lines = [minutes.heading, minutes.title, ""];
  if (minutes.attendees.length) lines.push(`Attendees: ${minutes.attendees.join(", ")}`, "");
  if (minutes.agenda.length) lines.push("Agenda", ...minutes.agenda.map((item, i) => `${i + 1}. ${item}`), "");
  if (minutes.discussion.length) {
    lines.push("Discussion");
    for (const point of minutes.discussion) lines.push(`- ${point.topic}: ${point.summary}`);
    lines.push("");
  }
  if (minutes.action_items.length) {
    lines.push("Action items", ...minutes.action_items.map((item) => `- ${item.task}${item.due ? ` (due ${item.due})` : ""}`));
  }
  return lines.join("\n").trim();
}

function initials(name: string): string {
  const words = name.split(/\s+/).filter(Boolean);
  return ((words[0]?.[0] ?? "") + (words.length > 1 ? words[words.length - 1][0] : "")).toUpperCase();
}

/** Stored in UTC; shown in the viewer's own time zone. */
function when(value: string | null): string {
  if (!value) return "—";
  const moment = new Date(value);
  return `${moment.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" })} · ${moment.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`;
}
